import { Fragment, useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { WMSFetch } from '../../utils/common-api-calls';
import LoadingSpinner from '../../../components/LoadingSpinner';
import TableSortButton from '../guidedInterface/components/TableSortButton';
import SFDynamicForm from '../sf-dynamic-form/Index.js';
import { invLookupDataArray } from './data.js';

const freshForm = () => {
    return JSON.parse(JSON.stringify(invLookupDataArray));
};

export default function InvLookupModal({ open, setOpen, currentUser, setSelectedInvLine }) {
    const [loading, setLoading] = useState(false);
    const [invLookupData, setInvLookupData] = useState([]);
    const [arrowDirections, setArrowDirections] = useState({ quantity: { direction: 'desc' } });
    const [hasSearched, setHasSearched] = useState(false);
    const [formData, setFormData] = useState(() => freshForm());
    const [formKey, setFormKey] = useState(0);

    useEffect(() => {
        if (!open) {
            reqIdRef.current += 1;
            setInvLookupData([]);
            setHasSearched(false);
            setFormData(freshForm());
            setArrowDirections({ quantity: { direction: 'desc' } });
            setFormKey((k) => k + 1);
        }
    }, [open]);

    useEffect(() => {
        if (formData[0]?.value) {
            handleSearch();
        }
    }, [formData[0]?.value]);

    const handleTableSort = (name) => {
        setArrowDirections((prev) => {
            const currentDir = prev[name]?.direction || 'desc';
            const newDir = currentDir === 'asc' ? 'desc' : 'asc';
            setInvLookupData((prevData) => {
                const sorted = [...prevData].sort((a, b) => {
                    const aQty = Number(a.inventoryLineQty) || 0;
                    const bQty = Number(b.inventoryLineQty) || 0;
                    return newDir === 'desc' ? bQty - aQty : aQty - bQty;
                });
                return sorted;
            });
            return { [name]: { direction: newDir } };
        });
    };

    const reqIdRef = useRef(0);

    const handleSearch = async () => {
        if (!formData[0]?.value) return;
        const myReq = ++reqIdRef.current;
        setLoading(true);
        setHasSearched(true);
        const response = await WMSFetch(
            'wms/go-back/get-inventory-data-by-part-number',
            { partNumber: formData[0]?.value },
            currentUser
        );
        if (myReq !== reqIdRef.current) return;

        if (response.error) {
            setLoading(false);
            setInvLookupData([]);
            return;
        }
        setInvLookupData(response);
        setLoading(false);
    };

    const handleRowClick = (row) => {
        setSelectedInvLine(row.inventoryLineName);
        setOpen(false);
        setInvLookupData([]);
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as='div' className='relative z-10' onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter='ease-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in duration-200'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                >
                    <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
                </Transition.Child>

                <div className='overflow-y-auto fixed inset-0 z-10'>
                    <div className='flex justify-center items-end p-4 min-h-full text-center sm:items-center sm:p-0'>
                        <Transition.Child
                            as={Fragment}
                            enter='ease-out duration-300'
                            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                            enterTo='opacity-100 translate-y-0 sm:scale-100'
                            leave='ease-in duration-200'
                            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                        >
                            <Dialog.Panel className='relative overflow-hidden text-center transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:w-full sm:max-w-6xl h-[700px]'>
                                <div className='px-6 py-3 text-white bg-blue-primary'>
                                    <Dialog.Title as='h2' className='text-lg font-semibold'>
                                        Inventory Lookup
                                    </Dialog.Title>
                                </div>
                                <div className='px-6 py-6'>
                                    <div className='flex gap-2 justify-center mx-auto mb-4 max-w-md'>
                                        <SFDynamicForm key={formKey} formData={formData} setFormData={setFormData} />
                                    </div>
                                    {loading ? (
                                        <LoadingSpinner />
                                    ) : invLookupData.length > 0 ? (
                                        <div className='overflow-x-auto' style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                            <table className='relative w-full min-w-full text-center table-fixed'>
                                                <thead className='sticky top-0 z-20 text-white bg-blue-primary'>
                                                    <tr>
                                                        <th className='px-4 py-3 font-semibold text-center border-b'>
                                                            INV Name
                                                        </th>
                                                        <th className='px-4 py-3 font-semibold text-center border-b'>
                                                            Part Number
                                                        </th>
                                                        <th className='px-4 py-3 font-semibold text-center border-b'>
                                                            Description
                                                        </th>
                                                        <th className='px-4 py-3 font-semibold text-center border-b'>
                                                            Owner Code
                                                        </th>
                                                        <th className='px-4 py-3 font-semibold text-center border-b select-none'>
                                                            <div className='flex gap-1 justify-center items-center'>
                                                                Quantity Available
                                                                <TableSortButton
                                                                    name='quantity'
                                                                    arrowDirections={arrowDirections}
                                                                    setArrowDirections={() => handleTableSort('quantity')}
                                                                />
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invLookupData.map((row, idx) => (
                                                        <tr
                                                            key={row.inventoryLineId || idx}
                                                            className='hover:bg-blue-100 cursor-pointer border-b last:!border-none'
                                                            onClick={() => handleRowClick(row)}
                                                        >
                                                            <td className='px-4 py-2 border-b'>{row.inventoryLineName}</td>
                                                            <td className='px-4 py-2 border-b'>{row.partName}</td>
                                                            <td className='px-4 py-2 border-b'>{row.description}</td>
                                                            <td className='px-4 py-2 border-b'>{row.ownerCode}</td>
                                                            <td className='px-4 py-2 border-b'>{row.inventoryLineQty}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : hasSearched ? (
                                        <p>No inventory data found</p>
                                    ) : null}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
